import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

export function LegalPage() {
  const { i18n } = useTranslation();
  
  const getText = (fr, ar, en) => {
    if (i18n.language === 'fr') return fr;
    if (i18n.language === 'ar') return ar;
    return en;
  };

  return (
    <div data-testid="legal-page" className="bg-[#F8F6F3] min-h-screen">
      {/* Hero */}
      <section className="bg-[#1a1a1a] py-24">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {getText('Mentions Légales', 'الإشعار القانوني', 'Legal Notice')}
            </h1>
            <p className="text-white/60">
              {getText('Informations légales relatives au site', 'المعلومات القانونية المتعلقة بالموقع', 'Legal information regarding this website')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container-main max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            {/* Éditeur */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Éditeur du site', 'ناشر الموقع', 'Website Publisher')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-700 mb-2"><strong>D.One SAS</strong></p>
                <p className="text-gray-600 mb-1">Société par Actions Simplifiée</p>
                <p className="text-gray-600 mb-1">Capital social : [À compléter]</p>
                <p className="text-gray-600 mb-1">RCS : [À compléter]</p>
                <p className="text-gray-600 mb-1">SIRET : [À compléter]</p>
                <p className="text-gray-600 mb-1">TVA Intracommunautaire : [À compléter]</p>
                <p className="text-gray-600 mt-4">
                  <strong>Siège social :</strong><br/>
                  [Adresse à compléter]<br/>
                  France
                </p>
                <p className="text-gray-600 mt-4">
                  <strong>Contact :</strong><br/>
                  Email : contact@simplyd.one<br/>
                  Téléphone : +33 4 84 25 29 44
                </p>
              </div>
            </div>

            {/* Directeur de publication */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Directeur de la publication', 'مدير النشر', 'Publication Director')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600">[Nom du directeur de publication à compléter]</p>
                <p className="text-gray-600">En qualité de Président de D.One SAS</p>
              </div>
            </div>

            {/* Hébergeur */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Hébergement', 'الاستضافة', 'Hosting')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600">
                  Ce site est hébergé par :<br/>
                  [Nom de l'hébergeur]<br/>
                  [Adresse de l'hébergeur]
                </p>
              </div>
            </div>

            {/* Propriété intellectuelle */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Propriété intellectuelle', 'الملكية الفكرية', 'Intellectual Property')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600 mb-4">
                  {getText(
                    "L'ensemble du contenu de ce site (textes, images, vidéos, logos, marques, etc.) est la propriété exclusive de D.One SAS ou de ses partenaires. Toute reproduction, représentation, modification, publication, adaptation de tout ou partie des éléments du site est interdite sans l'autorisation écrite préalable de D.One SAS.",
                    "جميع محتويات هذا الموقع (النصوص والصور ومقاطع الفيديو والشعارات والعلامات التجارية وما إلى ذلك) هي ملكية حصرية لـ D.One SAS أو شركائها.",
                    "All content on this site (texts, images, videos, logos, trademarks, etc.) is the exclusive property of D.One SAS or its partners. Any reproduction, representation, modification, publication, or adaptation of all or part of the site elements is prohibited without the prior written authorization of D.One SAS."
                  )}
                </p>
              </div>
            </div>

            {/* Limitation de responsabilité */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Limitation de responsabilité', 'تحديد المسؤولية', 'Limitation of Liability')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600 mb-4">
                  {getText(
                    "D.One SAS s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur ce site. Toutefois, D.One SAS ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition sur ce site.",
                    "تسعى D.One SAS لضمان دقة وتحديث المعلومات المنشورة على هذا الموقع. ومع ذلك، لا تستطيع D.One SAS ضمان دقة أو اكتمال المعلومات المتاحة على هذا الموقع.",
                    "D.One SAS strives to ensure the accuracy and updating of information published on this site. However, D.One SAS cannot guarantee the accuracy, precision, or completeness of the information made available on this site."
                  )}
                </p>
              </div>
            </div>

            {/* Droit applicable */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-3">
                <span className="w-2 h-8 bg-[#f4b00f] rounded-full"></span>
                {getText('Droit applicable', 'القانون المعمول به', 'Applicable Law')}
              </h2>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <p className="text-gray-600">
                  {getText(
                    "Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux français seront seuls compétents.",
                    "تخضع هذه الإشعارات القانونية للقانون الفرنسي. في حالة النزاع، ستكون المحاكم الفرنسية وحدها المختصة.",
                    "These legal notices are subject to French law. In case of dispute, French courts shall have sole jurisdiction."
                  )}
                </p>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center py-8 border-t border-gray-200">
              <p className="text-gray-500">
                © 2026 D.One SAS. {getText('Tous droits réservés.', 'جميع الحقوق محفوظة.', 'All rights reserved.')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default LegalPage;
